import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import { ref } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useCommon from '@/views/organization/useCommon'
import useCommonDeclaration from '../useCommonDeclaration'

export default function useDeclarationEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { getItem } = useLambda()
  const {
    config, updateDocumentMetadata, collection, sendMetadataEditedEmail,
  } = useCommonDeclaration()
  const { formatDate } = useCommon()
  const userData = store.state?.userStore?.userData
  const document = ref({})
  const originalDocument = ref({}) // Copia sin transformar para detección de cambios
  const isSubmitting = ref(false)

  async function show() {
    isSubmitting.value = true

    try {
      const query = {
        _id: oid(route.value.params.id),
        client_id: oid(userData.client.$oid),
        deleted: { $ne: true },
      }

      const item = await getItem({ collection, query })

      if (!item) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        router.push({ name: `${config.routeName}-list` })
        return
      }

      // Inicializar history si no existe (documentos legacy con auditLog)
      if (!item.history) {
        item.history = item.auditLog || [] // Migrar de auditLog si existe, o inicializar vacío
      }

      // Guardar copia SIN transformar para detección de cambios (ObjectIds puros)
      originalDocument.value = JSON.parse(JSON.stringify(item))

      // Transformar para el formulario
      document.value = {
        ...item,
        trabajador: item.trabajador?.toString() || null,
        area: item.area?.toString() || null,
        responsable: item.responsable?.toString() || null,
        empresas: item.empresas?.map(c => c.toString()) || [],
        fechaReunion: item.fechaReunion ? new Date(item.fechaReunion).toISOString().split('T')[0] : null,
        fechaProximaRevision: item.fechaProximaRevision ? new Date(item.fechaProximaRevision).toISOString().split('T')[0] : null,
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('declaration.error_fetch'))
    } finally {
      isSubmitting.value = false
    }
  }

  async function update(data) {
    isSubmitting.value = true

    try {
      // Incluir TODOS los campos para detección correcta de cambios
      const payload = {
        area: data.area?.value ? oid(data.area.value) : null,
        tipoDocumento: data.tipoDocumento?.value || '',
        tematica: data.tematica?.value || '',
        responsable: data.responsable?.value ? oid(data.responsable.value) : null,
        empresas: data.empresas?.value ? data.empresas.value.map(c => oid(c)) : [],
        observaciones: data.observaciones?.value || '',
        fechaReunion: data.fechaReunion?.value ? date(formatDate(data.fechaReunion.value)) : null,
        fechaProximaRevision: data.fechaProximaRevision?.value ? date(formatDate(data.fechaProximaRevision.value)) : null,
      }

      // Campo principal: depende del tipo de declaración
      if (config.mainFieldKind === 'text') {
        // Supplier: nombre como texto libre
        payload.nombre = data.nombre?.value || ''
      } else {
        // Lobby/Collaborator: trabajador como ObjectId
        payload.trabajador = data.trabajador?.value ? oid(data.trabajador.value) : null
      }

      // Institución solo para Lobby
      if (config.showInstitucion) {
        payload.institucion = data.institucion?.value || ''
      }

      const previousResponsable = document.value.responsable
      const newResponsable = data.responsable?.value

      // Pasar copia profunda para evitar mutaciones de Vue refs
      const originalDataCopy = JSON.parse(JSON.stringify(originalDocument.value))

      // Excluir 'nombre' del tracking cuando el campo principal es 'trabajador'
      // (el backend puede actualizarlo automáticamente basado en el worker seleccionado)
      const excludeFields = config.mainFieldKind === 'worker' ? ['nombre'] : []

      await updateDocumentMetadata(route.value.params.id, payload, originalDataCopy, excludeFields)

      // Send email notification
      if (newResponsable) {
        const responsableChanged = previousResponsable !== newResponsable
        sendMetadataEditedEmail(route.value.params.id, {
          nombre: data.nombre?.value,
          trabajador: payload.trabajador,
          estado: document.value.estado,
          fechaProximaRevision: data.fechaProximaRevision?.value,
        }, newResponsable, responsableChanged)
      }

      showSuccessMessage(i18n.t('declaration.updated_success'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('declaration.error_update'))
    } finally {
      router.push({ name: `${config.routeName}-list` })
      isSubmitting.value = false
    }
  }

  return {
    update,
    show,
    document,
    isSubmitting,
  }
}
